'use client';
import React, { useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Phone, Loader2, CheckCircle2, AlertCircle, ShieldCheck, KeyRound, RefreshCw, Eye, EyeOff } from 'lucide-react';

type ForgotStep = 'phone' | 'otp' | 'new-pin' | 'success';

interface ForgotPinFlowProps {
  onBack: () => void;
}

export default function ForgotPinFlow({ onBack }: ForgotPinFlowProps) {
  const [step, setStep] = useState<ForgotStep>('phone');
  const [isLoading, setIsLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [devToken, setDevToken] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [pinError, setPinError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [role, setRole] = useState('');
  const newPinRef = useRef<HTMLInputElement>(null);
  const confirmPinRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<{ phone: string }>();

  const startResendCooldown = useCallback(() => {
    setResendCooldown(120);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handlePhoneSubmit = async (data: { phone: string }) => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-pin/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: data.phone }),
      });
      const result = await res.json();

      if (!res.ok) {
        if (res.status === 429) {
          setOtpError(result.error || 'Too many requests. Please wait before trying again.');
        }
        if (res.status !== 429) {
          setPhone(data.phone);
          startResendCooldown();
          setStep('otp');
        }
        setIsLoading(false);
        return;
      }

      setPhone(data.phone);
      if (result.dev_token) {
        setDevToken(result.dev_token);
      }
      startResendCooldown();
      setStep('otp');
      setTimeout(() => otpRef.current?.focus(), 100);
    } catch {
      setPhone(data.phone);
      startResendCooldown();
      setStep('otp');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async () => {
    setOtpError('');
    if (otp.length !== 6) {
      setOtpError('Enter the 6-digit OTP');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-pin/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, token: otp }),
      });
      const result = await res.json();

      if (!res.ok) {
        setOtpError(result.error || 'Invalid OTP. Please check and try again.');
        setIsLoading(false);
        return;
      }

      setRole(result.role || '');
      setStep('new-pin');
      setTimeout(() => newPinRef.current?.focus(), 100);
    } catch {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setIsLoading(true);
    setOtpError('');
    setOtp('');
    try {
      const res = await fetch('/api/auth/forgot-pin/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const result = await res.json();
      if (result.dev_token) setDevToken(result.dev_token);
      startResendCooldown();
    } catch {
      // Silent fail
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinReset = async () => {
    setPinError('');
    if (newPin.length !== 4) {
      setPinError('Enter a 4-digit PIN');
      newPinRef.current?.focus();
      return;
    }
    if (newPin !== confirmPin) {
      setPinError('PINs do not match');
      confirmPinRef.current?.focus();
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-pin/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, token: otp, newPin }),
      });
      const result = await res.json();

      if (!res.ok) {
        if (result.code === 'token_expired') {
          setPinError('OTP has expired. Please start over.');
          setTimeout(() => { setStep('phone'); setOtp(''); setNewPin(''); setConfirmPin(''); }, 2000);
        } else {
          setPinError(result.error || 'Failed to reset PIN. Please try again.');
        }
        setIsLoading(false);
        return;
      }

      setStep('success');
    } catch {
      setPinError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const roleLabel = role === 'owner' ? 'Owner' : role === 'staff' || role === 'trainer' ? 'Staff' : role === 'member' ? 'Member' : '';

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft size={14} />
        Back to Sign In
      </button>

      {/* ── Step 1: Phone ── */}
      {step === 'phone' && (
        <div className="fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <KeyRound size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="font-display text-display-lg text-foreground">Reset PIN</h2>
              <p className="text-xs text-muted-foreground">Enter your registered phone number</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            {(['phone','otp','new-pin'] as ForgotStep[]).map((s, i) => (
              <React.Fragment key={`step-indicator-${s}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step === s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  {i + 1}
                </div>
                {i < 2 && <div className="flex-1 h-0.5 bg-border" />}
              </React.Fragment>
            ))}
          </div>

          <form onSubmit={handleSubmit(handlePhoneSubmit)} className="space-y-4" noValidate>
            <div>
              <label htmlFor="fp-phone" className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">
                Phone Number
              </label>
              <div className="relative">
                <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="fp-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="9876543210"
                  autoComplete="tel"
                  className="w-full bg-input border border-border rounded-xl pl-9 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  {...register('phone', {
                    required: 'Phone number is required',
                    pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit mobile number' },
                  })}
                />
              </div>
              {errors.phone && <p className="mt-1 text-xs text-risk">{errors.phone.message}</p>}
            </div>

            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-muted/50 border border-border">
              <AlertCircle size={13} className="text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                A 6-digit OTP will be sent to your registered number. Works for Owner, Member, and Staff accounts.
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 gradient-primary text-white text-sm font-bold rounded-xl transition-all btn-press disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? <><Loader2 size={16} className="animate-spin" /> Sending OTP...</> : 'Send OTP'}
            </button>
          </form>
        </div>
      )}

      {/* ── Step 2: OTP Verification ── */}
      {step === 'otp' && (
        <div className="fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
              <ShieldCheck size={18} className="text-secondary" />
            </div>
            <div>
              <h2 className="font-display text-display-lg text-foreground">Verify OTP</h2>
              <p className="text-xs text-muted-foreground">Sent to +91 {phone}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            {(['phone','otp','new-pin'] as ForgotStep[]).map((s, i) => (
              <React.Fragment key={`step-indicator2-${s}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${s === 'phone' ? 'bg-success/20 text-success' : step === s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  {s === 'phone' ? '✓' : i + 1}
                </div>
                {i < 2 && <div className={`flex-1 h-0.5 ${s === 'phone' ? 'bg-success/30' : 'bg-border'}`} />}
              </React.Fragment>
            ))}
          </div>

          {devToken && (
            <div className="mb-4 p-3 rounded-lg bg-warning/10 border border-warning/30">
              <p className="text-xs font-semibold text-warning mb-1">Development Mode</p>
              <p className="text-xs text-warning">
                MSG91 SMS not configured. Your OTP is: <span className="font-mono font-bold tracking-widest">{devToken}</span>
              </p>
              <p className="text-xs text-warning/70 mt-1">Configure MSG91_AUTH_KEY in .env for production SMS delivery.</p>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="fp-otp" className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">
              Enter 6-Digit OTP
            </label>
            <input
              id="fp-otp"
              ref={otpRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setOtpError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleOtpVerify(); } }}
              placeholder="••••••"
              autoComplete="one-time-code"
              className={`w-full bg-input border rounded-xl px-4 py-3 text-center text-2xl font-bold text-foreground tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-ring transition-all ${otpError ? 'border-risk' : 'border-border'}`}
            />
            {otpError && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <AlertCircle size={12} className="text-risk shrink-0" />
                <p className="text-xs text-risk">{otpError}</p>
              </div>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Type the OTP using your keyboard. Backspace and paste work normally.</p>
          </div>

          <button
            onClick={handleOtpVerify}
            disabled={isLoading || otp.length !== 6}
            className="w-full h-12 gradient-primary text-white text-sm font-bold rounded-xl transition-all btn-press disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
          >
            {isLoading ? <><Loader2 size={16} className="animate-spin" /> Verifying...</> : 'Verify OTP'}
          </button>

          <div className="flex items-center justify-between">
            <button
              onClick={() => { setStep('phone'); setOtp(''); setOtpError(''); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Change number
            </button>
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0 || isLoading}
              className="flex items-center gap-1 text-xs text-secondary hover:text-secondary/80 disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw size={11} />
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: New PIN — native inputs, no custom keypad ── */}
      {step === 'new-pin' && (
        <div className="fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <KeyRound size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="font-display text-display-lg text-foreground">Set New PIN</h2>
              <p className="text-xs text-muted-foreground">
                {roleLabel ? `${roleLabel} account · ` : ''}Choose a secure 4-digit PIN
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            {(['phone','otp','new-pin'] as ForgotStep[]).map((s, i) => (
              <React.Fragment key={`step-indicator3-${s}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${s !== 'new-pin' ? 'bg-success/20 text-success' : 'bg-primary text-white'}`}>
                  {s !== 'new-pin' ? '✓' : i + 1}
                </div>
                {i < 2 && <div className={`flex-1 h-0.5 ${s !== 'new-pin' ? 'bg-success/30' : 'bg-border'}`} />}
              </React.Fragment>
            ))}
          </div>

          <div className="space-y-4 mb-4">
            {/* New PIN */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="fp-new-pin" className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  New PIN
                </label>
                <button
                  type="button"
                  onClick={() => { setShowNewPin(v => !v); setTimeout(() => newPinRef.current?.focus(), 50); }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showNewPin ? <EyeOff size={12} /> : <Eye size={12} />}
                  {showNewPin ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                id="fp-new-pin"
                ref={newPinRef}
                type={showNewPin ? 'text' : 'password'}
                inputMode="numeric"
                maxLength={4}
                value={newPin}
                onChange={e => { setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmPinRef.current?.focus(); } }}
                placeholder="4-digit PIN"
                autoComplete="new-password"
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all text-center tracking-[0.5em]"
              />
            </div>

            {/* Confirm PIN */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="fp-confirm-pin" className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  Confirm PIN
                </label>
                <button
                  type="button"
                  onClick={() => { setShowConfirmPin(v => !v); setTimeout(() => confirmPinRef.current?.focus(), 50); }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPin ? <EyeOff size={12} /> : <Eye size={12} />}
                  {showConfirmPin ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                id="fp-confirm-pin"
                ref={confirmPinRef}
                type={showConfirmPin ? 'text' : 'password'}
                inputMode="numeric"
                maxLength={4}
                value={confirmPin}
                onChange={e => { setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handlePinReset(); } }}
                placeholder="Confirm PIN"
                autoComplete="new-password"
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all text-center tracking-[0.5em]"
              />
            </div>
          </div>

          {pinError && (
            <div className="flex items-center gap-1.5 mb-3 px-3 py-2 rounded-lg bg-risk/10 border border-risk/30">
              <AlertCircle size={13} className="text-risk shrink-0" />
              <p className="text-xs text-risk">{pinError}</p>
            </div>
          )}

          <p className="text-xs text-muted-foreground mb-4">Use your keyboard to enter your PIN. Backspace works normally.</p>

          <button
            onClick={handlePinReset}
            disabled={isLoading || newPin.length !== 4 || confirmPin.length !== 4}
            className="w-full h-12 gradient-primary text-white text-sm font-bold rounded-xl transition-all btn-press disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? <><Loader2 size={16} className="animate-spin" /> Resetting PIN...</> : 'Reset PIN'}
          </button>
        </div>
      )}

      {/* ── Step 4: Success ── */}
      {step === 'success' && (
        <div className="fade-in text-center py-4">
          <div className="w-16 h-16 rounded-2xl bg-success/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-success" />
          </div>
          <h2 className="font-display text-display-lg text-foreground mb-2">PIN Reset!</h2>
          <p className="text-sm text-muted-foreground mb-2">
            Your PIN has been updated successfully.
          </p>
          {roleLabel && (
            <p className="text-xs text-muted-foreground mb-6">
              Sign in as <span className="font-semibold text-foreground">{roleLabel}</span> with your new PIN.
            </p>
          )}
          {!roleLabel && <p className="text-sm text-muted-foreground mb-6">You can now sign in with your new PIN.</p>}
          <button
            onClick={onBack}
            className="w-full h-12 gradient-primary text-white text-sm font-bold rounded-xl transition-all btn-press"
          >
            Sign In Now
          </button>
        </div>
      )}
    </div>
  );
}